/**
 * Deployment settings, read at run time (CSAGE-437).
 *
 * In the container image, apps/web/docker-entrypoint.sh writes
 * /runtime-config.js at start, which sets window.__SAGE_RUNTIME_CONFIG__ before
 * the bundle loads. nginx serves it no-cache, so a changed setting reaches every
 * browser on its next page load. The values used to be substituted into the
 * hashed bundle instead, which nginx serves `immutable` for a year — a returning
 * browser kept the old values (PR #129 review).
 *
 * Without the container (vite dev, tests) there is no runtime config, and the
 * ordinary Vite env vars apply: VITE_API_BASE_URL, VITE_PORTAL_ORIGINS.
 */

export interface SageRuntimeConfig {
  /** Origin the SPA calls for the API. Empty means same-origin. */
  apiBaseUrl?: string;
  /** Comma-separated portal origins trusted to embed Sage. Empty keeps the defaults. */
  portalOrigins?: string;
}

declare global {
  interface Window {
    __SAGE_RUNTIME_CONFIG__?: SageRuntimeConfig;
  }
}

const ENV_FALLBACK: Record<keyof SageRuntimeConfig, string> = {
  apiBaseUrl: 'VITE_API_BASE_URL',
  portalOrigins: 'VITE_PORTAL_ORIGINS',
};

/**
 * The runtime value when the container provided one — including an empty
 * string, which is a deliberate setting — otherwise the Vite env var.
 */
export function deploymentSetting(key: keyof SageRuntimeConfig): string | undefined {
  if (typeof window !== 'undefined') {
    const value = window.__SAGE_RUNTIME_CONFIG__?.[key];
    if (typeof value === 'string') return value;
  }
  return viteEnv(ENV_FALLBACK[key]);
}

/**
 * Vite's env, with process.env checked first: that is what vi.stubEnv()
 * mutates, while import.meta.env is a snapshot taken when the test server
 * starts. A browser has no `process`, so there the real build-time value wins.
 */
export function viteEnv(key: string): string | undefined {
  if (typeof process !== 'undefined' && process.env && key in process.env) {
    return process.env[key];
  }
  const value = (import.meta as unknown as { env?: Record<string, unknown> }).env?.[key];
  return typeof value === 'string' ? value : undefined;
}
